import React, {useCallback, useEffect, useState} from 'react';

import {EGoogleTagManagerEventSource} from 'coreClasses/analytics/providers/gtm/IGoogleTagManager';
import {EEventType} from 'coreClasses/events/IEvents';
import {isProductSearchHitModel} from 'coreClasses/search/ProductSearchHitModel';
import {useMedia, useSearchMgrInstance} from 'coreHooks';
import createCtx from 'coreUtils/createCtx';

import {useCategoryContext} from 'brand/app/pages/category/context/CategoryContext';
import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';

import {IProductHitContext, IProductHitProviderProps} from './IProductHitContext';

export const [useProductHitContext, ProductHitContext] = createCtx<IProductHitContext>();

export const ProductHitProvider = ({
	children,
	eventOnClick,
	position,
	initialProduct,
	sourceOverwritten,
}: React.PropsWithChildren<IProductHitProviderProps>) => {
	const {searchType, searchInstanceKey} = useCategoryContext(true) || {};
	const isSearchType = searchType === 'quickSearch';
	const searchMgr = useSearchMgrInstance({
		searchType,
		key: searchInstanceKey,
	});
	const searchModel = searchMgr.getStoredSearchModel();
	const cgid = searchModel?.getSelectedRefinementById('cgid');

	const {managerRepository} = useGeneralProvider();
	const [product, setProduct] = useState(initialProduct);

	const media = useMedia();
	const smallestMedium = media('<medium');

	const dataLayerEventOnClick = useCallback((product, position, source, sourceSpecification) => {
		if (product && !isProductSearchHitModel(product) && eventOnClick) {
			eventOnClick(product, position);
		}

		const categoriesMgr = managerRepository.getCategoriesMgr();
		const categoryId = product.getCategoryId() || product.getPrimaryCategoryId();
		const productCategory = categoriesMgr?.getCategory(categoryId);
		const productCategoryTree = productCategory?.getParentCategoryTree();
		let plpCategory = null;
		let sourceCategory = null;

		if (sourceSpecification) {
			sourceCategory = categoriesMgr.getCategory(sourceSpecification);
		}

		if (cgid) {
			plpCategory = managerRepository.getCategoriesMgr().getCategory(cgid);
		}

		const eventObject = {
			source: sourceOverwritten || (!isSearchType ? source : undefined),
			sourceSpecification,
			eventSourceCategoryId: isSearchType ? EGoogleTagManagerEventSource.SEARCH_RESULTS : productCategory?.getId(),
			eventSourceCategoryName: productCategory?.getName(),
			plp_category_id: plpCategory?.getId(),
			plp_category_name: plpCategory?.getName(),
			productData: {
				product,
				showPriceValueOnly: true,
				eventSourceCategoryId: sourceCategory?.getId() || productCategory?.getId(),
				eventSourceCategoryName: sourceCategory?.getName() || productCategory?.getName(),
				position: position || 0,
				attributes: {
					dimension9: product?.getSeason(),
					dimension10: product?.getPriceModel().isSalesPriceBookApplied() ? 'MD' : 'FP',
				},
				productCategoriesIds: productCategoryTree?.map((cat) => cat.getId()),
			},
		};

		managerRepository.getEventsMgr().emit(EEventType.PRODUCT_CLICK, eventObject);
	}, []);

	const resetProduct = () => setProduct(initialProduct);

	useEffect(resetProduct, [initialProduct]);

	useEffect(() => {
		if (smallestMedium && initialProduct?.getProductId() !== product?.getProductId()) {
			resetProduct();
		}
	}, [smallestMedium]);

	return (
		<ProductHitContext
			value={{
				dataLayerEventOnClick,
				position,
				product,
				setProduct,
				resetProduct,
			}}
		>
			{children}
		</ProductHitContext>
	);
};
